import React, { ReactNode, Component, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { withRouteData } from 'react-static';
import { InjectedIntl, InjectedIntlProps, injectIntl } from 'react-intl';
import SpotifyPlayer from 'react-spotify-player';
import YoutubePlayer from 'react-youtube-player';
import {
  ContentWrapper,
  ContentContainer,
  TwoColScrollLayout,
  TwoColStickyLayout,
  TwoColContentLayout,
  FlowStripeWrapper
} from '@flow/components/ui';
import { Event, IArtist } from 'types/IArtist';
import FlowStripe from '@flow/components/ticker/FlowStripe';
import em from 'polished/lib/helpers/em';
import Moment from 'react-moment';
import { Link } from 'react-static';
import SEOTags from '@flow/components/seo/SEOTags';
import { renderImageCarousel } from '@flow/scenes/default/ui';
import BasicButton from '@flow/components/ui/button/BasicButton';
import * as NavigationAction from '@flow/store/navigation/actions';
import { connect } from 'react-redux';
import {
  getVenueNameByID,
  groupByKey,
} from "@flow/utils/helperUtils";
import { TextAnimation } from '@flow/components/animation/TextAnimation';
import moment from 'moment';

const ArtistInfoHeader = styled.header`
  margin-bottom: 7.5em;
`;

const ArtistTitle = styled.h1`
  font-family: Flow-LightDisplay, sans-serif !important;
  text-align: center;
  margin-top: ${em(9.5, 10)};
  margin-bottom: ${em(2.5, 10)} !important;
`;

const ArtistEventData = styled.span`
  display: block;
  text-align: center;
  font-size: 2.1em;
  text-transform: uppercase;
  font-family: Flow-Poster-Bold, sans-serif;
`;

const ArtistVenueData = ArtistEventData.extend`
  margin-top: ${em(2.25, 2.1)};
  margin-bottom: ${em(1.5, 2.1)};
  
  &:first-child {
    margin-top: 0;
  }
`;

const ArtistEventDate = ArtistEventData.extend`
`;

const YoutubeVideoWrapper = styled.div` 
  position: relative;
  padding-bottom: 56.25%; /* 16:9 */
  height: 0;
  margin: 5em 0;
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const SomeLinks = styled.ul`
  margin: 5em 0;
  li {
    display: inline-block;
    margin-right: 1em;
    
    &:last-child {
      margin-right: 0;
    }
    
    a {
      font-family: Flow-Poster-Bold, sans-serif !important;
      font-feature-settings:"liga", "dlig";
      font-size: 4em;
      display: inline-block;
      padding-right: ${em(0.6, 4)};
      transition: color 0.3s ease-out;
      
      &:${props => props.theme.hoverProperty} {
        color: ${props => props.theme.highlightColor};
      }
      
      span {
        font-family: Flow-Poster-Bold, sans-serif !important;
      }
      
      svg {
        vertical-align: text-top;
      }
    }
  }
`;

interface Props {
  appLanguage: IAppLanguage;
  artist: IArtist;
  _setLanguage: any;
  intl: InjectedIntl;
}

interface State {
  canTitleAnimate: boolean;
}

class Artist extends Component<Props & InjectedIntlProps, State> {

  constructor(props: Props & InjectedIntlProps) {
    super(props);

    this.state = {
      canTitleAnimate: false
    };
  }

  componentWillMount() {
    const {
      appLanguage,
      _setLanguage,
      artist: {
        lang_versions
      }
    } = this.props;

    _setLanguage({ appLanguage, lang_versions });
  }

  componentWillReceiveProps({
    appLanguage,
    artist: {
      lang_versions
    },
  }: Props) {
    const {
      _setLanguage,
    } = this.props;

    _setLanguage({ appLanguage, lang_versions });
  }

  componentDidMount() {
    this.setState({ canTitleAnimate: true });
  }

  render() {
    const {
      appLanguage,
      artist,
    } = this.props;

    const {
      embed_urls,
      links
    } = artist;

    const { canTitleAnimate } = this.state;

    // ToDo: This data needs to be generated in the cms system
    const generateHardcodedArtistPageUrl = () => {
      return '/' + (appLanguage.code === 'fi' ? '' : appLanguage.code + '/') + 'artists';
    };

    return (
      <TwoColScrollLayout>
        <SEOTags artist={artist}/>
        <TwoColStickyLayout>
          { renderImageCarousel(artist.images) }
        </TwoColStickyLayout>
        <TwoColContentLayout>
          <FlowStripeWrapper>
            <FlowStripe animate={true} rotation={90} color={'#000000'} />
          </FlowStripeWrapper>
          <ContentWrapper>
            <BasicButton
              label={<FormattedMessage id={'artist.backButtonLabel'}/>}
              href={generateHardcodedArtistPageUrl()}
              showAsBackArrow={true}
            />
            <ContentContainer>
              <ArtistInfoHeader>
                <ArtistTitle>
                  <TextAnimation
                    text={artist.title} animateIn={canTitleAnimate}/>
                </ArtistTitle>
                {this._renderEventsData()}
              </ArtistInfoHeader>

              <ContentContainer dangerouslySetInnerHTML={{__html: artist.content_group.content}}/>

              {Boolean(embed_urls && embed_urls.youtube) && <YoutubeVideoWrapper>
                <YoutubePlayer
                  videoId={embed_urls.youtube}
                  playbackState='unstarted'
                  configuration={
                    {
                      showinfo: 0,
                      controls: 0
                    }
                  }
                />
              </YoutubeVideoWrapper>}

              {Boolean(embed_urls && embed_urls.spotify) &&
              <SpotifyPlayer
                uri={embed_urls.spotify}
                size={{
                  width: '100%',
                  height: 80,
                }}
                view={'coverart'}
                theme={'black'}
              />}
            </ContentContainer>
            <SomeLinks>
              {links && links.map((link: { link_address?: string, link_type?: string }, index: number) =>
                <li key={index}>
                  <Link to={link.link_address} target={'_blank'}>
                    {this._renderSomeIcon(link.link_type)}
                  </Link>
                </li>
              )}
            </SomeLinks>
          </ContentWrapper>
        </TwoColContentLayout>
      </TwoColScrollLayout>
    );
  }

  private _renderEventsData = (): ReactNode => {
    const {
      events
    } = this.props.artist;

    /* if (!events || events.length === 0) {
      <span><span className='dingbats'>⬟ </span>Tba<span className='dingbats'> ⬟</span></span>;
    } */

    const distinctVenues = groupByKey(events, 'venue_id');
    return Object.keys(distinctVenues).map((venueId: any, key: number): ReactNode => {
      const venue: string = getVenueNameByID(parseInt(venueId, 10));
      return (
        <Fragment key={key}>
          {venue !== 'TBA' && <ArtistVenueData>{venue}</ArtistVenueData>}
          {distinctVenues[venueId].map((event: Event, idx: number) => {
            return (
              <ArtistEventDate key={idx}>
                {this._renderEventData(event)}
              </ArtistEventDate>
            );
          })}
        </Fragment>
      );
    });
  };

  private _renderEventData = (event: Event): ReactNode => {
    if (!moment(event.date).isValid()) {
      return null;
    }

    const { appLanguage } = this.props;

    return (
      <div style={{ marginTop: '.25em'}}>
        <Moment locale={appLanguage.code} format={'ddd D.M.'} date={event.date}/>
        { event.time &&
        <span>
          <span className='dingbats'> ⬟ </span>
          {event.time}
        </span>
        }
      </div>
    );
  }

  private _renderSomeIcon = (type: string): ReactNode => {
      const format = (str: string) => {
        return <span>{str}</span>;
      };

      switch (type) {
        case 'facebook-sign':
          return format('[FACEBOOK]');
        case 'instagram':
          return format('[INSTAGRAM]');
        case 'youtube':
          return format('[YOUTUBE]');
        case 'spotify':
          return format('[SPOTIFY]');
        case 'share-sign':
          return format('[WWW]');
        case 'twitter-sign':
          return format('[TWITTER]');
        case 'soundcloud':
          return format('[SOUNDCLOUD]');
      }
  }
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    _setLanguage: ({appLanguage, lang_versions}: ISetLanguage) => {
      dispatch(NavigationAction.setLanguage(appLanguage, lang_versions));
    }
  }
};

export default injectIntl(connect(
  null,
  mapDispatchToProps
)(withRouteData(Artist)) as any);



// WEBPACK FOOTER //
// /Users/ek1z/dev/flowfestival-com/src/scenes/artist/Artist.tsx